import { isUndefined } from 'lodash-es';

import { useGridPlotSize } from '../../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { HorizontalCrosshair } from '../../../../core/components/crosshair/HorizontalCrosshair.js';
import { getMagnifierLabel } from '../../../../core/components/crosshair/utils/get-magnifier-label.js';
import { OVERLAY_PADDING } from '../../../../core/components/data-point/constants.js';
import { reduceResetExploreVisible } from '../../../../core/components/interactions-state-helpers/reducers/reset-reducers.js';
import { THRESHOLD_BAR_THICKNESS } from '../../../../core/components/threshold-bar/types/constants.js';
import { useApplyCallbackOnScroll } from '../../../../core/hooks/useApplyCallbackOnScroll.js';
import {
  useClosestBinsState,
  useExploreState,
} from '../../../hooks/store/selectors.js';
import { useSetState } from '../../../hooks/store/state-setters.js';
import { useHistogramChartOverlayRef } from '../../../hooks/useHistogramChartOverlayRef.js';
import { useHistogramFilteredData } from '../../../hooks/useHistogramFilteredData.js';
import { useHistogramThresholds } from '../../../hooks/useHistogramThresholds.js';
import { useHistogramYFormatter } from '../../../hooks/useHistogramYFormatter.js';
import { useHistogramYScales } from '../../../hooks/useHistogramYScales.js';
import { crosshairLayout } from '../HistogramOverlays.sty.js';

export const HistogramCrosshair = () => {
  const {
    units: [leftUnit, rightUnit],
  } = useHistogramFilteredData();
  const [leftFormatter, rightFormatter] = useHistogramYFormatter();
  const { pinned, position } = useExploreState();
  const setState = useSetState();
  const onScrollCallback = () => setState(reduceResetExploreVisible);

  useApplyCallbackOnScroll(pinned, onScrollCallback);

  const relativeMousePosition = position?.relative;
  const [leftScale, rightScale] = useHistogramYScales();
  const { width } = useGridPlotSize();
  const { leftThreshold, rightThreshold } = useHistogramThresholds();
  const closestBinsState = useClosestBinsState();

  const chartRef = useHistogramChartOverlayRef();
  const chartBounds = chartRef.current?.getBoundingClientRect();
  const leftLabel = pinned
    ? undefined
    : getMagnifierLabel(
        leftUnit,
        'y',
        leftFormatter,
        relativeMousePosition,
        leftScale,
      );

  const rightLabel = pinned
    ? undefined
    : getMagnifierLabel(
        rightUnit,
        'y',
        rightFormatter,
        relativeMousePosition,
        rightScale,
      );

  const hasPinnedBin = pinned && !isUndefined(closestBinsState?.closestBin);
  const canHover = !pinned && !isUndefined(relativeMousePosition);

  if (!hasPinnedBin && !canHover) {
    return null;
  }

  const y = hasPinnedBin
    ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      closestBinsState.closestBin!.y - OVERLAY_PADDING
    : relativeMousePosition?.y ?? 0;

  const hasLeftThresholds = leftThreshold && leftThreshold.length > 0;
  const hasRightThresholds = rightThreshold && rightThreshold.length > 0;

  const thresholdLeftSize = hasLeftThresholds ? THRESHOLD_BAR_THICKNESS : 0;
  const thresholdRightSize = hasRightThresholds ? THRESHOLD_BAR_THICKNESS : 0;
  const horizontalCrosshairWidth =
    width + thresholdLeftSize + thresholdRightSize;

  return (
    <svg className={crosshairLayout}>
      <HorizontalCrosshair
        data-testid="horizontal-crosshair-histogram"
        width={horizontalCrosshairWidth}
        leftLabel={leftLabel}
        rightLabel={rightLabel}
        leftOffset={thresholdLeftSize}
        rightOffset={thresholdRightSize}
        y={y}
        dashed={!pinned}
        chartBounds={chartBounds}
      />
    </svg>
  );
};
